import { useMemo, useState } from "react";
import {
  MDBCard,
  MDBCardBody,
  MDBCol,
  MDBInput,
  MDBTable,
} from "mdbreact";

const formatLastSync = (value) => {
  if (!value) return "Not synced yet";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not synced yet";

  return date.toLocaleString();
};

const formatExpiry = (value) => {
  if (!value) return "--";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "--";

  return date.toLocaleDateString();
};

const getAvailableUnits = (inventory = {}) => {
  const consumed = Array.isArray(inventory?.consumptions)
    ? inventory.consumptions.reduce(
        (sum, item) => sum + Number(item?.qnty || 0),
        0,
      )
    : 0;

  return Math.max(Number(inventory?.qnty || 0) - consumed, 0);
};

const getMedicineDosage = (menu = {}) => {
  if (menu?.dosage) return menu.dosage;
  if (String(menu?.itemType || "").toLowerCase() !== "meds") return "";
  if (!menu?.strength) return menu?.unit || "";

  return `${menu.strength} ${menu.measure || ""}/${menu.unit || "pcs"}`
    .replace(/\s+/g, " ")
    .trim();
};

const getMedicineLabel = (item = {}) => {
  const menu = item?.menu || {};
  const medicineName =
    menu?.abbreviation || menu?.description || menu?.name || "Unnamed medicine";
  const dosage = getMedicineDosage(menu);

  return dosage ? `${medicineName} (${dosage})` : medicineName;
};

const getClinicLabel = (item = {}) =>
  item?.clinic?.title || item?.clinic?.code || "Clinic";

const Stock = ({ collections = [], summary = {}, lastFetchedAt, isLoading }) => {
  const [keyword, setKeyword] = useState("");

  const rows = useMemo(() => {
    const search = String(keyword || "").trim().toLowerCase();

    const mapped = collections.map((item, index) => ({
      id: item?._id || `${item?.menu?._id || "menu"}-${index}`,
      clinic: getClinicLabel(item),
      medicine: getMedicineLabel(item),
      dosage: getMedicineDosage(item?.menu),
      batchNo: item?.batchNo || "--",
      available: getAvailableUnits(item),
      unit: item?.unit || "pcs",
      expiry: formatExpiry(item?.expiry),
      status: item?.status || "available",
      supplier: item?.supplier || "--",
    }));

    if (!search) return mapped;

    return mapped.filter((item) =>
      [
        item.clinic,
        item.medicine,
        item.dosage,
        item.batchNo,
        item.unit,
        item.status,
        item.supplier,
      ]
        .join(" ")
        .toLowerCase()
        .includes(search),
    );
  }, [collections, keyword]);

  return (
    <MDBCol md="12" className="mb-4">
      <MDBCard className="mb-3">
        <MDBCardBody>
          <div className="d-flex justify-content-between align-items-center flex-wrap mb-3">
            <div>
              <h5 className="mb-1 font-weight-bold">Medicine Stock List</h5>
              <div className="text-muted small">
                Searchable inventory list for clinic medicines and product stock.
              </div>
            </div>
            <div className="text-right small">
              <div className="font-weight-bold text-info">
                {summary?.totalAvailableUnits || 0} available units
              </div>
              <div className="text-muted">
                Last sync: {formatLastSync(lastFetchedAt)}
              </div>
            </div>
          </div>

          <div className="mb-3">
            <MDBInput
              label="Search medicine, clinic, batch, supplier"
              type="text"
              outline
              value={keyword}
              onChange={({ target }) => setKeyword(target.value)}
              icon="search"
            />
          </div>

          <div className="table-responsive">
            <MDBTable striped bordered hover>
              <thead>
                <tr>
                  <th>Clinic</th>
                  <th>Medicine</th>
                  <th>Batch</th>
                  <th>Available</th>
                  <th>Unit</th>
                  <th>Expiry</th>
                  <th>Status</th>
                  <th>Supplier</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan="8" className="text-center text-muted py-4">
                      Loading medicine stocks...
                    </td>
                  </tr>
                ) : rows.length ? (
                  rows.map((item) => (
                    <tr key={item.id}>
                      <td>{item.clinic}</td>
                      <td>{item.medicine}</td>
                      <td>{item.batchNo}</td>
                      <td>{item.available}</td>
                      <td>{item.unit}</td>
                      <td>{item.expiry}</td>
                      <td className="text-capitalize">{item.status}</td>
                      <td>{item.supplier}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="text-center text-muted py-4">
                      No medicine stock matched your search.
                    </td>
                  </tr>
                )}
              </tbody>
            </MDBTable>
          </div>
        </MDBCardBody>
      </MDBCard>
    </MDBCol>
  );
};

export default Stock;
